import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import {
  AdminContactInquiry,
  CONTACT_INQUIRY_STATUS_CLASSES,
  CONTACT_INQUIRY_STATUS_LABELS,
  ContactInquiryDetailDialogData,
  ContactInquiryStatus
} from '@app/model/contact.model';
import { ContactManagementDataService } from '@app/services/contact-management-data.service';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { ToastrService } from 'ngx-toastr';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'app-contact-inquiry-detail',
  templateUrl: './contact-inquiry-detail.component.html',
  styleUrls: ['./contact-inquiry-detail.component.scss']
})
export class ContactInquiryDetailComponent implements OnInit {
  isSaving = false;
  isDeleting = false;

  readonly statusLabels = CONTACT_INQUIRY_STATUS_LABELS;
  readonly statusClasses = CONTACT_INQUIRY_STATUS_CLASSES;
  readonly statusOptions: { value: ContactInquiryStatus; label: string }[] = [
    { value: 'new', label: CONTACT_INQUIRY_STATUS_LABELS.new },
    { value: 'in_progress', label: CONTACT_INQUIRY_STATUS_LABELS.in_progress },
    { value: 'resolved', label: CONTACT_INQUIRY_STATUS_LABELS.resolved },
    { value: 'closed', label: CONTACT_INQUIRY_STATUS_LABELS.closed }
  ];

  statusForm = new FormGroup({
    status: new FormControl('new', [Validators.required]),
    adminNotes: new FormControl('', [Validators.maxLength(2000)])
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ContactInquiryDetailDialogData,
    private dialogRef: MatDialogRef<ContactInquiryDetailComponent>,
    private contactManagementDataService: ContactManagementDataService,
    private layoutUtilsService: LayoutUtilsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.data && this.data.inquiry) {
      this.statusForm.patchValue({
        status: this.data.inquiry.status,
        adminNotes: this.data.inquiry.admin_notes || ''
      });
    }
  }

  get inquiry(): AdminContactInquiry {
    return this.data.inquiry;
  }

  get currentStatusLabel(): string {
    return this.statusLabels[this.inquiry.status] || this.inquiry.status;
  }

  get currentStatusClass(): string {
    return this.statusClasses[this.inquiry.status] || '';
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    return dd + '/' + mm + '/' + yyyy + ' ' + hh + ':' + mi;
  }

  closeModal(): void {
    if (this.isSaving || this.isDeleting) {
      return;
    }
    this.dialogRef.close();
  }

  saveStatus(): void {
    if (this.isSaving || this.statusForm.invalid) {
      this.statusForm.markAllAsTouched();
      return;
    }

    const value = this.statusForm.value;
    this.isSaving = true;

    this.contactManagementDataService
      .updateInquiryStatus(this.inquiry.id, {
        status: value.status,
        admin_notes: value.adminNotes || null
      })
      .pipe(
        take(1),
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(
              response && response.message ? response.message : 'Không thể cập nhật trạng thái'
            );
            return;
          }
          this.toastr.success('Cập nhật trạng thái thành công');
          this.dialogRef.close({
            updated: true,
            status: value.status,
            adminNotes: value.adminNotes || null
          });
        },
        (error) => {
          const errorResponse = error && error.error ? error.error : null;
          let message = 'Không thể cập nhật trạng thái';
          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }
          this.toastr.error(message);
        }
      );
  }

  confirmDelete(): void {
    if (this.isDeleting || this.isSaving) {
      return;
    }
    const title = 'Xóa thư liên hệ';
    const description =
      'Bạn có chắc chắn muốn xóa thư "' + (this.inquiry.subject || '') + '" từ "' + (this.inquiry.full_name || '') + '"?';
    const waitDescription = 'Đang xóa thư liên hệ...';

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDescription);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.deleteInquiry();
      });
  }

  private deleteInquiry(): void {
    this.isDeleting = true;
    this.contactManagementDataService
      .deleteInquiry(this.inquiry.id)
      .pipe(
        take(1),
        finalize(() => {
          this.isDeleting = false;
        })
      )
      .subscribe(
        (response) => {
          if (!response || !response.success) {
            this.toastr.error(response && response.message ? response.message : 'Không thể xóa thư liên hệ');
            return;
          }
          this.toastr.success('Xóa thư liên hệ thành công');
          this.dialogRef.close({ deleted: true });
        },
        (error) => {
          const errorResponse = error && error.error ? error.error : null;
          let message = 'Không thể xóa thư liên hệ';
          if (errorResponse && errorResponse.message) {
            message = errorResponse.message;
          } else if (errorResponse && errorResponse.title) {
            message = errorResponse.title;
          }
          this.toastr.error(message);
        }
      );
  }
}
